import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import { API, defaultLimitPagination, get } from "../../../config";
import Spin from "../../common/Spin";
import Pagination from "../../common/Pagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import ImportLogDetail from "./ImportLogDetail";
import { debounce } from "underscore";
import moment from "moment";
import SearchBox from "../../common/SearchBox";

const ImportedLogs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [importLogs, setImportLogs] = useState({});
  const [loading, setLoading] = useState(false);
  const [importLogId, setImportLogId] = useState();
  const [importLogModal, setImportLogModal] = useState(false);
  const search = query.get("search");
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");

  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const params = useParams();
  const projectId = params?.project_id;
  const trustType = params?.trust_type;
  const trust_type = params?.trust_type === "project" ? 1 : 0;

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const toggleShowModal = () => {
    setImportLogModal(!importLogModal);
  };

  const handleDetails = (id) => {
    setImportLogId(id);
    setImportLogModal(true);
  };

  // *API Call for Import Logs List - Start
  const fetchImportedLogs = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.COA_IMPORT_LOGS}?builder_project_id=${
            projectId ? projectId : ""
          }&trust_type=${trust_type}&limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&status=1&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }`
        );
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        setTotal(data?.data?.total);
        setImportLogs(data?.data?.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, trust_type, searchParam, sortField, sortOrder]
  );

  useEffect(() => {
    fetchImportedLogs();
  }, [fetchImportedLogs]);
  // *API Call for Import Logs List - End

  useEffect(() => {
    navigate(
      `/projects/${projectId}/chart-of-account-import-logs?tab=imported&limit=${
        limit ? limit : defaultLimitPagination
      }&page=${page ? page : 1}&search=${searchParam ? searchParam : ""}`,
      { replace: true }
    );
  }, [navigate, projectId, page, limit, trustType, searchParam]);

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  const getVerifiedDateTime = (date) => {
    return moment(date).format("DD-MM-YYYY HH:mm:ss").toString();
  };

  // *Import Logs List Table Row and Column Generation, Filtering and Sorting of Record, Select Row and Expand Row - Start
  const importLogsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: importLogs[i]?.id,
        id: importLogs[i]?.id ? importLogs[i]?.id : "",
        name: importLogs[i]?.name ? `${importLogs[i]?.name}` : "",
        type: importLogs[i]?.type === 0 ? "Debit" : "Credit",
        account_type: importLogs[i]?.account_type
          ? importLogs[i]?.account_type
          : "",
        tax_rate:
          importLogs[i]?.tax === 1
            ? "GST " +
              (importLogs[i]?.gst_percentage
                ? importLogs[i]?.gst_percentage
                : 0) +
              "%"
            : importLogs[i]?.tax === 2
            ? "BAS Excluded"
            : importLogs[i]?.tax === 3
            ? "GST on Imports"
            : "GST Excluded",
        code: importLogs[i]?.code ? importLogs[i]?.code : "-",
        sub_code: importLogs[i]?.sub_code ? importLogs[i]?.sub_code : "-",
        accounting_code: importLogs[i]?.accounting_code
          ? importLogs[i]?.accounting_code
          : "-",
        import_from: importLogs[i]?.import_from
          ? importLogs[i]?.import_from
          : "BuildTrust",
        last_sync_date: importLogs[i]?.last_sync_date
          ? getVerifiedDateTime(importLogs[i]?.last_sync_date)
          : "-",
        action: (
          <>
            <FontAwesomeIcon
              icon={faEye}
              size="1x"
              onClick={() => handleDetails(importLogs[i]?.id)}
            />
          </>
        ),
      });
    }
    return items;
  };

  const importLogsData = importLogsGenerator(importLogs?.length);

  const columns = [
    {
      dataField: "name",
      text: "Name",
      sort: true,
    },
    {
      dataField: "code",
      text: "Code",
    },
    {
      dataField: "sub_code",
      text: "Sub Code",
    },
    {
      dataField: "account_type",
      text: "Account Type",
      sort: true,
    },
    {
      dataField: "type",
      text: "Type",
    },
    {
      dataField: "tax_rate",
      text: "Tax Rate",
    },
    {
      dataField: "accounting_code",
      text: "Accounting Code",
      sort: true,
    },
    {
      dataField: "import_from",
      text: "Import From",
    },
    {
      dataField: "last_sync_date",
      text: "Last synchronised time",
    },
    {
      dataField: "action",
      text: "Action",
      className: "text-center",
      style: { width: "97px" },
    },
  ];

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchImportedLogs(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchImportedLogs(limit, page);
  };

  // *Import Logs List Table Row and Column Generation, Filtering and Sorting of Record, Select Row and Expand Row - End

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  return (
    <>
      <div className="table-top-btn">
        <SearchBox
          placeholder={"Search Imported Logs"}
          searchParamData={searchParamData}
          handleSearchChange={handleSearchChange}
          handleClearSearch={handleClearSearch}
        />
      </div>
      <div className="audit-statement">
        {loading && <Spin />}
        <BootstrapTable
          keyField="batch_id"
          remote
          data={importLogsData}
          columns={columns}
          onTableChange={handleTableChange}
          noDataIndication="No Data Found"
        />

        <Pagination
          total={total}
          limit={parseInt(limit)}
          currentPage={page}
          updateLimit={handleUpdateLimit}
          updatePage={handleChangePage}
          from={from}
          to={to}
        />
      </div>

      {importLogModal && (
        <ImportLogDetail
          toggleShowModal={toggleShowModal}
          importLogId={importLogId}
          importLogModal={importLogModal}
          projectId={projectId}
        />
      )}
    </>
  );
};

export default ImportedLogs;
